import twMerge from 'twMerge';

import Button from 'molecules/Button';
import Section from 'molecules/Section';

import { type HeadingProps } from 'components/Heading';
import headingStyles, {
  buttonContainer,
  headingContent,
  headingEyebrow,
  headingSubhead,
} from 'components/OffsetSwitcher/OffsetSwitcher.styles';
import OffsetSwitcherV1 from 'components/OffsetSwitcher/components/v1';
import OffsetSwitcherV2 from 'components/OffsetSwitcher/components/v2';

import getTheme from 'utils/getTheme';
import richTextParser from 'utils/richTextParser/richTextParser';

import type { ContentfulComponentOffsetSwitcher } from 'graphqlTypes';
import type { SectionProps } from 'molecules/Section';
import type { FC } from 'react';
import type { RichText } from 'utils/richTextParser/richTextParser';

export type OffsetSwitcherProps = ContentfulComponentOffsetSwitcher & SectionProps;

const OffsetSwitcher: FC<OffsetSwitcherProps> = ({
  background,
  contentful_id,
  offsetSwitcherHeading,
  references,
  sectionIdLink,
}) => {
  const theme = getTheme(background || 'white');

  const getContent = (props: HeadingProps, version: 'v1' | 'v2') => {
    const { eyebrow, heading, headingSize, subheading, richText, buttons } = props;
    const H = offsetSwitcherHeading?.headingAs || 'h2';

    return (
      <div className={twMerge(headingStyles({ version }), `offset-switcher-heading-${version}`)}>
        {eyebrow && <p className={twMerge(headingEyebrow({ theme }))}>{eyebrow}</p>}
        {heading && <H className={twMerge(headingContent({ theme, size: headingSize, version }))}>{heading}</H>}
        {subheading && <p className={twMerge(headingSubhead({ theme, version }))}>{subheading}</p>}
        {richText && (
          <p className={twMerge(headingSubhead({ theme, version }))}>
            {richTextParser(richText as RichText, undefined, theme)}
          </p>
        )}
        {version !== 'v2' && buttons && (
          <div className={twMerge(buttonContainer())}>
            {buttons.map(button => button && <Button {...button} key={button.internalName} />)}
          </div>
        )}
      </div>
    );
  };

  return (
    <Section id={sectionIdLink} background={background} contained={false} contentfulId={contentful_id}>
      <div className="flex flex-col justify-between gap-8 lg:gap-12">
        {offsetSwitcherHeading && getContent(offsetSwitcherHeading, 'v1')}
        {offsetSwitcherHeading && getContent(offsetSwitcherHeading, 'v2')}
        <OffsetSwitcherV1 references={references} theme={theme} />
        <OffsetSwitcherV2 references={references} background={background} theme={theme} />
      </div>
    </Section>
  );
};

export default OffsetSwitcher;
